// client/src/hooks/useBlobStorage.js

import { useState, useCallback } from 'react';
import blobStorageAPI from '../api/blobStorageAPI';

const useBlobStorage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [knowledgePosts, setKnowledgePosts] = useState([]);
  const [isKnowledgeLoaded, setIsKnowledgeLoaded] = useState(false);
  const [knowledgePostsCount, setKnowledgePostsCount] = useState(0);
  
  // ============================================================================
  // FILE DOWNLOAD & VIEW
  // ============================================================================

  const getFileDownloadUrl = useCallback((fileId) => {
    return blobStorageAPI.getFileDownloadUrl(fileId);
  }, []);

  const getFileViewUrl = useCallback((fileId) => {
    return blobStorageAPI.getFileViewUrl(fileId);
  }, []);

  const downloadFile = useCallback(async (fileId, requestedBy) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.downloadFile(fileId, requestedBy);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const viewFile = useCallback(async (fileId, requestedBy) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.viewFile(fileId, requestedBy);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const getFileMetadata = useCallback(async (fileId, requestedBy) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.getFileMetadata(fileId, requestedBy);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const triggerDownload = useCallback(async (fileId, filename, requestedBy) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.triggerDownload(fileId, filename, requestedBy);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const openFileInNewTab = useCallback((fileId) => {
    blobStorageAPI.openFileInNewTab(fileId);
  }, []);

  // ============================================================================
  // FILE RETRIEVAL
  // ============================================================================

  const getAttachments = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.getAttachments(filters);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const getStudentAssessmentAttachments = useCallback(async (studentId, assessmentType, assessmentId, courseId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.getStudentAssessmentAttachments(
        studentId,
        assessmentType,
        assessmentId,
        courseId
      );
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const getQuestionAttachments = useCallback(async (studentId, questionId, courseId, assessmentType, assessmentId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.getQuestionAttachments(
        studentId,
        questionId,
        courseId,
        assessmentType,
        assessmentId
      );
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const getCourseAttachments = useCallback(async (courseId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.getCourseAttachments(courseId);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const getStudentAttachments = useCallback(async (studentId) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.getStudentAttachments(studentId);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const getAttachmentsByUploader = useCallback(async (uploadedBy) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.getAttachmentsByUploader(uploadedBy);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const getFileStatistics = useCallback(async (filters = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.getFileStatistics(filters);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  // ============================================================================
  // FILE UPLOAD
  // ============================================================================

  const uploadFile = useCallback(async (file, metadata) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.uploadFile(file, metadata);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const uploadMultipleFiles = useCallback(async (files, metadata) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.uploadMultipleFiles(files, metadata);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  // ============================================================================
  // FILE DELETION
  // ============================================================================

  const deleteFile = useCallback(async (fileId, deletedBy) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.deleteFile(fileId, deletedBy);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const deleteMultipleFiles = useCallback(async (fileIds, deletedBy) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.deleteMultipleFiles(fileIds, deletedBy);
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);



  // ============================================================================
  // KNOWLEDGE POSTS
  // ============================================================================

  const listKnowledgePosts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.listKnowledgePosts();
      if (response.success) {
        setKnowledgePostsCount(response.number || 0);
      }
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const loadKnowledgePosts = useCallback(async (count) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.loadKnowledgePosts(count);
      if (response.success) {
        setKnowledgePosts(response.files || []);
        setIsKnowledgeLoaded(true);
      }
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      setIsKnowledgeLoaded(true);
      return { success: false, error: err.message };
    }
  }, []);

  const updateKnowledgePost = useCallback(async (loginId, filename, updateData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.updateKnowledgePost(loginId, filename, updateData);
      if (response.success) {
        // Reload posts after update
        const listResult = await blobStorageAPI.listKnowledgePosts();
        if (listResult.success && listResult.number > 0) {
          const loadResult = await blobStorageAPI.loadKnowledgePosts(listResult.number);
          if (loadResult.success) {
            setKnowledgePosts(loadResult.files || []);
          }
        }
      }
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const deleteKnowledgePost = useCallback(async (loginId, filename) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.deleteKnowledgePost(loginId, filename);
      if (response.success) {
        // Reload posts after delete
        const listResult = await blobStorageAPI.listKnowledgePosts();
        if (listResult.success) {
          setKnowledgePostsCount(listResult.number || 0);
          if (listResult.number > 0) {
            const loadResult = await blobStorageAPI.loadKnowledgePosts(listResult.number);
            if (loadResult.success) {
              setKnowledgePosts(loadResult.files || []);
            }
          } else {
            setKnowledgePosts([]);
          }
        }
      }
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  const createKnowledgePost = useCallback(async (postData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blobStorageAPI.createKnowledgePost(postData);
      if (response.success) {
        // Reload posts after create
        const listResult = await blobStorageAPI.listKnowledgePosts();
        if (listResult.success) {
          setKnowledgePostsCount(listResult.number || 0);
          if (listResult.number > 0) {
            const loadResult = await blobStorageAPI.loadKnowledgePosts(listResult.number);
            if (loadResult.success) {
              setKnowledgePosts(loadResult.files || []);
            }
          }
        }
      }
      setLoading(false);
      return response;
    } catch (err) {
      setError(err.message);
      setLoading(false);
      return { success: false, error: err.message };
    }
  }, []);

  // ============================================================================
  // CLEAR ERROR
  // ============================================================================

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // ============================================================================
  // RETURN ALL METHODS AND STATE
  // ============================================================================

  return {
    // State
    loading,
    error,
    knowledgePosts,
    isKnowledgeLoaded,
    knowledgePostsCount,
    clearError,

    // Download & View
    getFileDownloadUrl,
    getFileViewUrl,
    downloadFile,
    viewFile,
    getFileMetadata,
    triggerDownload,
    openFileInNewTab,
    
    // Retrieval
    getAttachments,
    getStudentAssessmentAttachments,
    getQuestionAttachments,
    getCourseAttachments,
    getStudentAttachments,
    getAttachmentsByUploader,
    getFileStatistics,
    
    // Upload
    uploadFile,
    uploadMultipleFiles,
    
    // Deletion
    deleteFile,
    deleteMultipleFiles,

    // knowledge
    listKnowledgePosts,
    loadKnowledgePosts,
    updateKnowledgePost,
    deleteKnowledgePost,
    createKnowledgePost,
  };
};

export default useBlobStorage;